import React, { useState, useEffect, useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  Customized,
} from "recharts";
import {
  Activity,
  Watch,
  RefreshCw,
  Users,
  ArrowRight,
  Layers,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import TeamStatCard from "../TeamOverview/TeamStatCard";

const initialsOf = (name) =>
  (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";

const TEAM_COLOR = "#E07A3A";
const PLAYER_COLOR = "#4F46E5";

// Server-side re-bucketing options (all multiples of the native 30s window).
const RESOLUTIONS = [
  { seconds: 30, label: "30s" },
  { seconds: 60, label: "1m" },
  { seconds: 300, label: "5m" },
  { seconds: 900, label: "15m" },
];

// Intensity zones for the per-window view, as per-athlete load (Σ linear g)
// in one native 30s window. Scaled by the bucket size so "intense" means the
// same thing at 30s and 5m. Provisional cut-offs — tune once we have data
// from real sessions.
const ZONES = [
  {
    key: "light",
    labelKey: "session.load.zones.light",
    max: 45,
    color: "#22C55E",
  },
  {
    key: "medium",
    labelKey: "session.load.zones.medium",
    max: 100,
    color: "#F59E0B",
  },
  {
    key: "intense",
    labelKey: "session.load.zones.intense",
    max: Infinity,
    color: "#EF4444",
  },
];
const ZONE_FILL_OPACITY = 0.055;

// Fixed skeleton heights (% of plot area) so the shimmer doesn't jitter.
const SKELETON_HEIGHTS = [
  22, 38, 55, 48, 70, 84, 62, 76, 58, 44, 66, 52, 36, 28, 40, 24,
];

// Pick a sensible default bucket from the session length so a 2-hour
// session doesn't open on 240 hairline bars.
const defaultBucketFor = (startTime, endTime) => {
  if (!startTime || !endTime) return 60;
  const mins = (new Date(endTime) - new Date(startTime)) / 60000;
  if (!isFinite(mins) || mins <= 0) return 60;
  if (mins <= 20) return 30;
  if (mins <= 75) return 60;
  return 300;
};

// Elapsed-time tick label: "0m", "2:30", "1h05".
const formatElapsed = (seconds, bucketSeconds) => {
  const sign = seconds < 0 ? "-" : "";
  const abs = Math.abs(Math.round(seconds));
  const h = Math.floor(abs / 3600);
  const m = Math.floor((abs % 3600) / 60);
  const s = abs % 60;
  if (h > 0) return `${sign}${h}h${String(m).padStart(2, "0")}`;
  if (bucketSeconds < 60 && s !== 0)
    return `${sign}${m}:${String(s).padStart(2, "0")}`;
  return `${sign}${m}m`;
};

const formatBinLabel = (edges, i) => {
  const lo = edges[i];
  const hi = edges[i + 1];
  return hi === undefined ? `${lo}g+` : `${lo}–${hi}g`;
};

const sumCounts = (counts) => (counts || []).reduce((a, c) => a + c, 0);

const emptyBucket = (nBins) => ({
  sum_g: 0,
  peak_g: null,
  counts: Array(nBins).fill(0),
});

const SessionLoadChart = ({
  workspaceId,
  teamId,
  sessionId,
  sessionStartTime,
  sessionEndTime,
  selectedPlayerId,
  selectedPlayerName,
  onClearSelection,
  // Render inside another card (e.g. a tab): no outer card, no title, and a
  // slim subtitle + controls row instead of a bordered header.
  embedded = false,
}) => {
  const { t } = useTranslation();
  const shellClass = embedded
    ? ""
    : "rounded-xl border border-[#F3F4F6] bg-white";
  const bodyClass = embedded ? "" : "p-4";
  const [bucket, setBucket] = useState(() =>
    defaultBucketFor(sessionStartTime, sessionEndTime)
  );
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  // Bumped by "Try again" to re-run the fetch effect without changing inputs.
  const [reloadKey, setReloadKey] = useState(0);
  // "window": load per bucket (spikes in intense phases). "cumulative": running
  // total that climbs during play and plateaus at breaks.
  const [view, setView] = useState("window");
  const isCumulative = view === "cumulative";
  // Series hidden via the legend: "team" and/or "player".
  const [hidden, setHidden] = useState(() => new Set());
  // Zone under the pointer in the intensity legend: that zone's band and the
  // matching stretches of the curve light up, the rest fade.
  const [hoverZone, setHoverZone] = useState(null);
  const toggleHidden = (key) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  useEffect(() => {
    if (!workspaceId || !teamId || !sessionId) return;
    let active = true;
    setLoading(true);
    setError(false);
    betaAPI
      .getSessionLoadGraph({ workspaceId, teamId, sessionId, bucket })
      .then((res) => {
        if (active) setGraph(res.data || null);
      })
      .catch((err) => {
        console.error("Failed to fetch session load graph", err);
        if (active) setError(true);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [workspaceId, teamId, sessionId, bucket, reloadKey]);

  const binEdges = graph?.bin_edges || [];
  const bucketSeconds = graph?.bucket_seconds || bucket;

  const selectedSeries = useMemo(() => {
    if (!graph || selectedPlayerId == null) return null;
    return (
      graph.players.find(
        (p) => String(p.player_id) === String(selectedPlayerId)
      ) || null
    );
  }, [graph, selectedPlayerId]);

  // Build a contiguous time grid from the first to last team window so gaps
  // render as zero rather than being interpolated across.
  const data = useMemo(() => {
    if (!graph || !graph.team_windows?.length) return [];
    const nBins = binEdges.length;
    const step = bucketSeconds * 1000;

    const teamByStart = new Map(
      graph.team_windows.map((w) => [new Date(w.start).getTime(), w])
    );
    const playerByStart = new Map(
      (selectedSeries?.windows || []).map((w) => [
        new Date(w.start).getTime(),
        w,
      ])
    );

    // How many athletes reported in each bucket, so a single athlete can be
    // compared against the per-athlete team average rather than the team sum
    // (which is ~N times larger and flattens their curve onto the axis).
    const activeByStart = new Map();
    for (const p of graph.players || []) {
      for (const w of p.windows || []) {
        const k = new Date(w.start).getTime();
        activeByStart.set(k, (activeByStart.get(k) || 0) + 1);
      }
    }

    const starts = [...teamByStart.keys()];
    const first = Math.min(...starts);
    const last = Math.max(...starts);
    const origin = sessionStartTime
      ? new Date(sessionStartTime).getTime()
      : first;

    const rows = [];
    for (let t = first; t <= last; t += step) {
      const team = teamByStart.get(t) || emptyBucket(nBins);
      const player = playerByStart.get(t) || emptyBucket(nBins);
      const active = activeByStart.get(t) || 0;
      const elapsed = (t - origin) / 1000;
      rows.push({
        t,
        elapsed,
        label: formatElapsed(elapsed, bucketSeconds),
        activeCount: active,
        teamAvgLoad: active ? team.sum_g / active : 0,
        teamLoad: team.sum_g,
        teamEvents: sumCounts(team.counts),
        teamPeak: team.peak_g,
        teamCounts: team.counts,
        playerLoad: selectedSeries ? player.sum_g : undefined,
        playerEvents: selectedSeries ? sumCounts(player.counts) : undefined,
        playerPeak: selectedSeries ? player.peak_g : undefined,
        playerCounts: selectedSeries ? player.counts : undefined,
      });
    }
    // Running totals for the cumulative view.
    let teamCum = 0;
    let teamAvgCum = 0;
    let playerCum = 0;
    for (const r of rows) {
      teamCum += r.teamLoad;
      teamAvgCum += r.teamAvgLoad;
      playerCum += r.playerLoad || 0;
      r.teamCum = teamCum;
      r.teamAvgCum = teamAvgCum;
      r.playerCum = selectedSeries ? playerCum : undefined;
    }
    return rows;
  }, [graph, binEdges, bucketSeconds, selectedSeries, sessionStartTime]);

  const hasData = data.length > 0;
  const hasSelectedPlayer = !!selectedSeries;
  // A player is selected in the list but has no load windows in this session.
  const selectedHasNoLoad = selectedPlayerId != null && !selectedSeries;

  // Zone thresholds at the current bucket size, and which zone a value falls in.
  const zoneScale = bucketSeconds / 30;
  const zones = ZONES.map((z) => ({
    ...z,
    label: t(z.labelKey),
    max: z.max * zoneScale,
  }));
  const zoneFor = (value) =>
    zones.find((z) => value < z.max) || zones[zones.length - 1];

  // Share of windows spent in each zone for the focused series (athlete if
  // selected, else team average) — the one-line read of how hard it was.
  const zoneShares = useMemo(() => {
    if (!data.length) return null;
    const key = selectedSeries ? "playerLoad" : "teamAvgLoad";
    const counts = { light: 0, medium: 0, intense: 0 };
    for (const r of data) counts[zoneFor(r[key] || 0).key] += 1;
    return ZONES.map((z) => ({
      ...z,
      label: t(z.labelKey),
      pct: Math.round((counts[z.key] / data.length) * 100),
    }));
  }, [data, selectedSeries, bucketSeconds, t]);

  // Minutes per athlete: played (any window with load), and time spent in the
  // high and medium intensity zones. Team figures are the average across the
  // athletes who reported. Session length is the span of the time grid.
  const minutes = useMemo(() => {
    if (!graph || !data.length) return null;
    const perWindowMin = bucketSeconds / 60;
    const tally = (windows) => {
      const t = { played: 0, high: 0, medium: 0 };
      for (const w of windows || []) {
        const g = w.sum_g || 0;
        if (g <= 0) continue;
        t.played += perWindowMin;
        const z = zoneFor(g).key;
        if (z === "intense") t.high += perWindowMin;
        else if (z === "medium") t.medium += perWindowMin;
      }
      return t;
    };
    const perPlayer = (graph.players || []).map((p) => tally(p.windows));
    const n = perPlayer.length || 1;
    const teamAvg = perPlayer.reduce(
      (a, t) => ({
        played: a.played + t.played / n,
        high: a.high + t.high / n,
        medium: a.medium + t.medium / n,
      }),
      { played: 0, high: 0, medium: 0 }
    );
    return {
      session: data.length * perWindowMin,
      teamAvg,
      player: selectedSeries ? tally(selectedSeries.windows) : null,
    };
  }, [graph, data, selectedSeries, bucketSeconds]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    const focus = hasSelectedPlayer ? "player" : "team";
    const counts = row[`${focus}Counts`] || [];
    const peak = row[`${focus}Peak`];
    const windowEnd = formatElapsed(row.elapsed + bucketSeconds, bucketSeconds);
    const zone = isCumulative
      ? null
      : zoneFor((hasSelectedPlayer ? row.playerLoad : row.teamAvgLoad) || 0);

    return (
      <div className="min-w-[180px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
        <div className="mb-1.5 flex items-center justify-between gap-3">
          <p className="text-[13px] font-semibold text-[#111827]">
            {row.label} – {windowEnd}
          </p>
          {zone && (
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{ color: zone.color, backgroundColor: `${zone.color}14` }}
            >
              {zone.label}
            </span>
          )}
        </div>
        <div className="space-y-1.5 border-t border-[#F3F4F6] pt-1.5">
          {hasSelectedPlayer && !hidden.has("player") && (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded"
                  style={{ backgroundColor: PLAYER_COLOR }}
                />
                <span className="text-[11px] text-[#6B7280]">
                  {isCumulative
                    ? t("session.load.tooltip.playerSoFar", {
                        name: selectedPlayerName,
                      })
                    : selectedPlayerName}
                </span>
              </div>
              <span className="text-xs font-semibold text-[#111827]">
                {Math.round(
                  isCumulative ? row.playerCum : row.playerLoad
                ).toLocaleString()}
                <span className="ml-0.5 text-[10px] font-normal text-[#6B7280]">
                  g
                </span>
              </span>
            </div>
          )}
          {!hidden.has("team") && (
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded"
                  style={{ backgroundColor: TEAM_COLOR }}
                />
                <span className="text-[11px] text-[#6B7280]">
                  {t(
                    isCumulative
                      ? "session.load.tooltip.teamAvgSoFar"
                      : "session.load.tooltip.teamAvg",
                    { count: row.activeCount }
                  )}
                </span>
              </div>
              <span className="text-xs font-semibold text-[#111827]">
                {Math.round(
                  isCumulative ? row.teamAvgCum : row.teamAvgLoad
                ).toLocaleString()}
                <span className="ml-0.5 text-[10px] font-normal text-[#6B7280]">
                  g
                </span>
              </span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px] text-[#9CA3AF]">
              {isCumulative
                ? t("session.load.tooltip.teamTotalSoFar")
                : t("session.load.tooltip.teamTotal")}
            </span>
            <span className="text-[11px] text-[#6B7280]">
              {Math.round(
                isCumulative ? row.teamCum : row.teamLoad
              ).toLocaleString()}
              g
            </span>
          </div>
        </div>
        {/* Bin breakdown for the focused series */}
        <div className="mt-2 border-t border-[#F3F4F6] pt-2">
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wide text-[#9CA3AF]">
              {hasSelectedPlayer
                ? t("session.load.tooltip.athleteEvents")
                : t("session.load.tooltip.teamEvents")}
            </span>
            {peak != null && (
              <span className="text-[10px] text-[#9CA3AF]">
                {t("session.load.tooltip.peak", {
                  value: Number(peak).toFixed(1),
                })}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-x-3 gap-y-0.5">
            {counts.map((c, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-1.5"
              >
                <span className="text-[10px] text-[#9CA3AF]">
                  {formatBinLabel(binEdges, i)}
                </span>
                <span className="text-[10px] font-medium text-[#374151]">
                  {c}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const segmentClass = (active) =>
    `rounded-md px-2 py-1 text-[11px] font-medium transition duration-150 ${
      active
        ? "bg-white text-[#111827] shadow-sm"
        : "text-[#6B7280] hover:text-[#374151]"
    }`;

  const nAthletes = graph?.players?.length || 0;

  // ---- Layout pieces. Every state (loading / error / empty / data) renders
  // the same skeleton of rows so switching tabs or finishing a fetch never
  // shifts anything: tiles → legend+controls row → 220px chart → footer slot.

  // View (per window / cumulative) and bucket resolution.
  const Controls = () => (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-0.5 rounded-lg bg-[#F3F4F6] p-0.5">
        {[
          { key: "window", label: t("session.load.views.window") },
          { key: "cumulative", label: t("session.load.views.cumulative") },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => setView(v.key)}
            className={segmentClass(view === v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-0.5 rounded-lg bg-[#F3F4F6] p-0.5">
        {RESOLUTIONS.map((r) => (
          <button
            key={r.seconds}
            onClick={() => setBucket(r.seconds)}
            disabled={loading}
            className={segmentClass(bucket === r.seconds)}
          >
            {r.label}
          </button>
        ))}
      </div>
    </div>
  );

  // Legend — click an entry to hide/show that series.
  const Legend = () => (
    <div className="flex items-center gap-4">
      {hasSelectedPlayer && (
        <button
          onClick={() => toggleHidden("player")}
          className="flex items-center gap-1.5 transition-opacity duration-150"
          style={{ opacity: hidden.has("player") ? 0.4 : 1 }}
          title={
            hidden.has("player")
              ? t("session.load.legend.showAthlete")
              : t("session.load.legend.hideAthlete")
          }
        >
          <span
            className="h-2.5 w-2.5 rounded transition-colors duration-150"
            style={{
              backgroundColor: hidden.has("player") ? "#D1D5DB" : PLAYER_COLOR,
            }}
          />
          <span
            className="text-xs"
            style={{
              color: hidden.has("player") ? "#9CA3AF" : "#6B7280",
              textDecoration: hidden.has("player") ? "line-through" : "none",
            }}
          >
            {selectedPlayerName}
          </span>
        </button>
      )}
      <button
        onClick={() => toggleHidden("team")}
        className="flex items-center gap-1.5 transition-opacity duration-150"
        style={{ opacity: hidden.has("team") ? 0.4 : 1 }}
        title={
          hidden.has("team")
            ? t("session.load.legend.showTeam")
            : t("session.load.legend.hideTeam")
        }
      >
        {hasSelectedPlayer ? (
          <span
            className="h-0.5 w-5 rounded-full"
            style={{
              backgroundImage: `repeating-linear-gradient(90deg, ${
                hidden.has("team") ? "#D1D5DB" : TEAM_COLOR
              } 0, ${
                hidden.has("team") ? "#D1D5DB" : TEAM_COLOR
              } 4px, transparent 4px, transparent 7px)`,
            }}
          />
        ) : (
          <span
            className="h-2.5 w-2.5 rounded transition-colors duration-150"
            style={{
              backgroundColor: hidden.has("team") ? "#D1D5DB" : TEAM_COLOR,
            }}
          />
        )}
        <span
          className="text-xs"
          style={{
            color: hidden.has("team") ? "#9CA3AF" : "#6B7280",
            textDecoration: hidden.has("team") ? "line-through" : "none",
          }}
        >
          {t("session.load.legend.teamAvgPerAthlete")}
        </span>
      </button>
    </div>
  );

  // Legend on the left, controls on the right. Same slot as the impact tabs'
  // legend row.
  const ControlsRow = ({ left }) => (
    <div className="mb-3 flex min-h-[28px] flex-wrap items-center justify-between gap-2">
      <div className="text-xs text-[#9CA3AF]">{left}</div>
      <Controls />
    </div>
  );

  // Same height as the impact tabs' "View Impact List" button row so the
  // card bottom stays put when switching tabs.
  const FooterSlot = ({ children }) => (
    <div className="mt-4 flex h-[34px] items-center justify-center">
      <span className="text-xs text-[#9CA3AF]">{children}</span>
    </div>
  );

  const Header = () =>
    embedded ? null : (
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-[#111827]">
            {t("session.load.title")}
          </h3>
          <p className="text-xs text-[#6B7280]">
            {selectedPlayerId != null && selectedPlayerName
              ? t("session.load.subtitlePlayer", {
                  name: selectedPlayerName,
                  resolution:
                    RESOLUTIONS.find((r) => r.seconds === bucket)?.label ||
                    bucket,
                })
              : t(
                  isCumulative
                    ? "session.load.subtitleAccumulated"
                    : "session.load.subtitlePerWindow",
                  { threshold: binEdges[0] ?? 3 }
                )}
          </p>
        </div>
      </div>
    );

  // Three stat cards, same component and footprint as the impact tabs.
  // mode: "loading" | "ghost" | "data"
  // Three minutes cards: total played, high intensity, medium intensity.
  // Athlete view compares the intensity minutes with the team average
  // ("5 below" + a % badge); team view shows the average per athlete.
  const Tiles = ({ mode }) => {
    const dash = mode !== "data" || selectedHasNoLoad || !minutes;
    const athleteMode = hasSelectedPlayer || selectedHasNoLoad;
    const focus = minutes ? minutes.player || minutes.teamAvg : null;
    const min = (v) => `${Math.round(v)}`;
    const pctOf = (part, whole) =>
      whole > 0 ? `${Math.round((part / whole) * 100)}%` : "–";

    // Athlete vs team average for an intensity band.
    const compare = (key) => {
      if (!athleteMode || !minutes?.player) return null;
      const mine = minutes.player[key];
      const avg = minutes.teamAvg[key];
      const diff = Math.round(mine - avg);
      const pct = avg > 0 ? Math.round(((mine - avg) / avg) * 100) : null;
      return { diff, pct, avg };
    };

    const intensityCard = (label, key) => {
      const cmp = compare(key);
      return (
        <TeamStatCard
          label={label}
          loading={mode === "loading"}
          value={dash ? "–" : min(focus[key])}
          unit={dash ? undefined : t("session.load.tiles.minUnit")}
          footerLeft={
            mode === "ghost"
              ? t("session.load.tiles.noDataYet")
              : athleteMode
              ? t("session.load.tiles.teamAvgMinutes", {
                  value: dash ? "–" : min(minutes.teamAvg[key]),
                })
              : t("session.load.tiles.avgPerAthlete")
          }
          footerRight={
            mode !== "data" || dash
              ? ""
              : cmp
              ? cmp.diff === 0
                ? t("session.load.tiles.onAverage")
                : t(
                    cmp.diff > 0
                      ? "session.load.tiles.minutesAbove"
                      : "session.load.tiles.minutesBelow",
                    { value: Math.abs(cmp.diff) }
                  )
              : t("session.load.tiles.ofPlayedTime", {
                  pct: pctOf(focus[key], focus.played),
                })
          }
          footerRightColor={
            cmp && cmp.diff !== 0
              ? cmp.diff > 0
                ? "#E07A3A"
                : "#10B981"
              : "#111827"
          }
          peerComparison={
            cmp && cmp.pct != null && !dash
              ? { value: cmp.pct, peerValue: cmp.avg }
              : null
          }
        />
      );
    };

    return (
      <div className="mb-4 grid grid-cols-3 gap-3">
        <TeamStatCard
          label={t("session.load.tiles.totalMinutesPlayed")}
          loading={mode === "loading"}
          value={dash ? "–" : min(focus.played)}
          unit={dash ? undefined : t("session.load.tiles.minUnit")}
          footerLeft={
            mode === "ghost"
              ? t("session.load.tiles.noDataYet")
              : dash
              ? t("session.load.tiles.session")
              : t("session.load.tiles.sessionMinutes", {
                  value: min(minutes.session),
                })
          }
          footerRight={
            mode !== "data" || dash
              ? ""
              : t("session.load.tiles.ofSession", {
                  pct: pctOf(focus.played, minutes.session),
                })
          }
        />
        {intensityCard(t("session.load.tiles.highIntensity"), "high")}
        {intensityCard(t("session.load.tiles.mediumIntensity"), "medium")}
      </div>
    );
  };

  if (loading && !graph) {
    return (
      <div className={shellClass}>
        <Header />
        <div className={bodyClass}>
          <Tiles mode="loading" />
          <ControlsRow left={<div className="shimmer h-2.5 w-28 rounded" />} />
          <div className="flex h-[220px] flex-col">
            <div className="flex min-h-0 flex-1 gap-2">
              <div className="flex w-5 flex-col justify-between py-0.5">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="shimmer h-1.5 w-3.5 rounded" />
                ))}
              </div>
              <div className="flex flex-1 items-end gap-1 border-b border-[#F3F4F6]">
                {SKELETON_HEIGHTS.map((h, i) => (
                  <div
                    key={i}
                    className="shimmer w-full rounded-t"
                    style={{ height: `${h}%` }}
                  />
                ))}
              </div>
            </div>
            <div className="mt-2 flex gap-1 pl-7">
              {SKELETON_HEIGHTS.map((_, i) => (
                <div key={i} className="shimmer h-1.5 flex-1 rounded" />
              ))}
            </div>
          </div>
          <FooterSlot>
            <span className="shimmer inline-block h-2.5 w-40 rounded" />
          </FooterSlot>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={shellClass}>
        <Header />
        <div className={bodyClass}>
          <Tiles mode="ghost" />
          <ControlsRow left={null} />
          <div className="flex h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-4 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-[#FEF3E7]">
              <Activity size={28} className="text-[#E07A3A]" />
            </div>
            <h4 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("session.load.error.title")}
            </h4>
            <p className="mb-3 max-w-[260px] text-xs leading-relaxed text-[#6B7280]">
              {t("session.load.error.body")}
            </p>
            <button
              onClick={() => setReloadKey((k) => k + 1)}
              className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-white px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
            >
              <RefreshCw size={12} />
              {t("common.actions.tryAgain")}
            </button>
          </div>
          <FooterSlot />
        </div>
      </div>
    );
  }

  // Nothing recorded for the whole session: ghost tiles, a faux flat-line
  // chart, and a short strip explaining how load data gets here.
  if (!hasData) {
    return (
      <div className={shellClass}>
        <Header />
        <div className={bodyClass}>
          <Tiles mode="ghost" />
          <ControlsRow left={null} />
          <div className="relative flex h-[220px] flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-4 text-center">
            {/* Faux flat-line chart behind the copy */}
            <svg
              className="pointer-events-none absolute inset-x-4 bottom-6 h-24 w-[calc(100%-2rem)]"
              viewBox="0 0 400 96"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="ghostLoadFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#E07A3A" stopOpacity={0.1} />
                  <stop offset="100%" stopColor="#E07A3A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <path
                d="M0 88 C40 84 60 60 100 66 S160 90 200 70 S260 30 300 58 S360 86 400 80 L400 96 L0 96 Z"
                fill="url(#ghostLoadFill)"
              />
              <path
                d="M0 88 C40 84 60 60 100 66 S160 90 200 70 S260 30 300 58 S360 86 400 80"
                fill="none"
                stroke="#E07A3A"
                strokeWidth="1.5"
                strokeOpacity="0.25"
                strokeDasharray="4 4"
              />
              <line
                x1="0"
                y1="95"
                x2="400"
                y2="95"
                stroke="#E5E7EB"
                strokeWidth="1.5"
              />
            </svg>

            {/* Illustration */}
            <div className="relative mb-3">
              <div
                className="absolute left-1/2 top-1/2 h-24 w-24 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  background:
                    "radial-gradient(circle, rgba(232, 133, 74, 0.14) 0%, rgba(232, 133, 74, 0) 70%)",
                }}
              />
              <div className="relative flex h-14 w-14 items-center justify-center rounded-xl bg-[#FEF3E7]">
                <Activity size={28} className="text-[#E07A3A]" />
              </div>
              <div className="absolute -bottom-1 -right-2 flex h-6 w-6 items-center justify-center rounded-lg border border-[#F3F4F6] bg-white shadow-sm">
                <Watch size={12} className="text-[#9CA3AF]" />
              </div>
            </div>

            <h4 className="relative mb-1 text-sm font-semibold text-[#111827]">
              {t("session.load.empty.title")}
            </h4>
            <p className="relative mb-4 max-w-[300px] text-xs leading-relaxed text-[#6B7280]">
              {t("session.load.empty.body")}
            </p>

            {/* How it gets here */}
            <div className="relative flex items-center gap-2 rounded-full border border-[#F3F4F6] bg-white px-3 py-1.5 text-[11px] text-[#6B7280] shadow-sm">
              <span className="flex items-center gap-1">
                <Watch size={11} className="text-[#E07A3A]" />
                {t("session.load.empty.stepRecords", {
                  threshold: binEdges[0] ?? 3,
                })}
              </span>
              <ArrowRight size={10} className="text-[#D1D5DB]" />
              <span className="flex items-center gap-1">
                <Layers size={11} className="text-[#E07A3A]" />
                {t("session.load.empty.stepSynced")}
              </span>
              <ArrowRight size={10} className="text-[#D1D5DB]" />
              <span className="flex items-center gap-1">
                <Activity size={11} className="text-[#E07A3A]" />
                {t("session.load.empty.stepGraph")}
              </span>
            </div>
          </div>
          <FooterSlot />
        </div>
      </div>
    );
  }

  return (
    <div className={shellClass}>
      <Header />
      <div className={bodyClass}>
        <Tiles mode="data" />
        <ControlsRow left={<Legend />} />

        {/* Chart */}
        <div className="relative h-[220px]">
          {/* Selected athlete has no windows: keep the team curve as context
              but dim it and explain, with a one-click way back to the team. */}
          {selectedHasNoLoad && (
            <div className="absolute inset-0 z-10 flex items-center justify-center">
              <div className="flex max-w-[320px] flex-col items-center rounded-xl border border-[#F3F4F6] bg-white/95 px-5 py-4 text-center shadow-lg backdrop-blur-sm">
                <div className="relative mb-2.5">
                  <div
                    className="flex h-11 w-11 items-center justify-center rounded-full text-sm font-semibold text-white"
                    style={{ backgroundColor: PLAYER_COLOR }}
                  >
                    {initialsOf(selectedPlayerName)}
                  </div>
                  <div className="absolute -bottom-1 -right-1.5 flex h-5 w-5 items-center justify-center rounded-md border border-[#F3F4F6] bg-white shadow-sm">
                    <Watch size={10} className="text-[#9CA3AF]" />
                  </div>
                </div>
                <h4 className="mb-0.5 text-sm font-semibold text-[#111827]">
                  {t("session.load.noPlayerLoad.title", {
                    name: selectedPlayerName,
                  })}
                </h4>
                <p className="mb-3 text-xs leading-relaxed text-[#6B7280]">
                  {t("session.load.noPlayerLoad.body")}
                </p>
                {onClearSelection && (
                  <button
                    onClick={onClearSelection}
                    className="flex items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-3 py-1.5 text-xs font-medium text-[#374151] transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <Users size={12} />
                    {t("session.load.noPlayerLoad.showAll")}
                  </button>
                )}
              </div>
            </div>
          )}
          <div
            className="h-full transition-opacity duration-150"
            style={{
              opacity: loading ? 0.5 : selectedHasNoLoad ? 0.35 : 1,
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={data}
                margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="teamLoadFill" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="0%"
                      stopColor={TEAM_COLOR}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="100%"
                      stopColor={TEAM_COLOR}
                      stopOpacity={0.03}
                    />
                  </linearGradient>
                  <linearGradient
                    id="playerLoadFill"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop
                      offset="0%"
                      stopColor={PLAYER_COLOR}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="100%"
                      stopColor={PLAYER_COLOR}
                      stopOpacity={0.03}
                    />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#F3F4F6"
                />
                {/* Intensity zones — faint bands behind the curve, per-window
                    view only (a running total has no intensity). */}
                {!isCumulative &&
                  zones.map((z, i) => (
                    <ReferenceArea
                      key={z.key}
                      y1={i === 0 ? 0 : zones[i - 1].max}
                      y2={isFinite(z.max) ? z.max : undefined}
                      fill={z.color}
                      fillOpacity={
                        hoverZone == null
                          ? ZONE_FILL_OPACITY
                          : hoverZone === z.key
                          ? 0.16
                          : 0.02
                      }
                      stroke="none"
                      ifOverflow="visible"
                      label={{
                        value: z.label,
                        position: "insideTopRight",
                        fontSize: 10,
                        fill: z.color,
                        fillOpacity:
                          hoverZone == null || hoverZone === z.key ? 0.9 : 0.3,
                        dx: -4,
                        dy: 2,
                      }}
                    />
                  ))}
                {/* Zone-coloured stroke and fill for the primary curve: a
                    vertical gradient in plot pixels with hard stops at the
                    zone thresholds, so each stretch of the line takes the
                    colour of the band it sits in. A hovered legend zone keeps
                    its colour while the others fade to grey. */}
                {!isCumulative && (
                  <Customized
                    component={(chart) => {
                      const axis = Object.values(chart.yAxisMap || {})[0];
                      const off = chart.offset;
                      if (!axis?.scale || !off) return null;
                      const top = off.top;
                      const bottom = off.top + off.height;
                      const span = bottom - top || 1;
                      const at = (v) =>
                        Math.min(1, Math.max(0, (axis.scale(v) - top) / span));
                      const colourFor = (z) =>
                        hoverZone == null || hoverZone === z.key
                          ? z.color
                          : "#D1D5DB";
                      // Top → bottom: intense, medium, light.
                      const [light, medium, intense] = zones;
                      const stops = [
                        [0, intense],
                        [at(medium.max), intense],
                        [at(medium.max), medium],
                        [at(light.max), medium],
                        [at(light.max), light],
                        [1, light],
                      ];
                      return (
                        <defs>
                          <linearGradient
                            id="zoneStroke"
                            gradientUnits="userSpaceOnUse"
                            x1={0}
                            y1={top}
                            x2={0}
                            y2={bottom}
                          >
                            {stops.map(([o, z], i) => (
                              <stop
                                key={i}
                                offset={o}
                                stopColor={colourFor(z)}
                              />
                            ))}
                          </linearGradient>
                          <linearGradient
                            id="zoneFill"
                            gradientUnits="userSpaceOnUse"
                            x1={0}
                            y1={top}
                            x2={0}
                            y2={bottom}
                          >
                            {stops.map(([o, z], i) => (
                              <stop
                                key={i}
                                offset={o}
                                stopColor={colourFor(z)}
                                stopOpacity={
                                  hoverZone != null && hoverZone !== z.key
                                    ? 0.04
                                    : 0.14
                                }
                              />
                            ))}
                          </linearGradient>
                        </defs>
                      );
                    }}
                  />
                )}
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#9CA3AF" }}
                  dy={8}
                  minTickGap={24}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#9CA3AF" }}
                  tickFormatter={(v) => `${v}`}
                  dx={-5}
                  // Keep the top of the "intense" band in view even for a
                  // light session, so a low curve reads as low.
                  domain={
                    isCumulative
                      ? [0, "auto"]
                      : [
                          0,
                          (dataMax) =>
                            Math.ceil(
                              Math.max(dataMax, zones[1].max * 1.3) / 10
                            ) * 10,
                        ]
                  }
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{ stroke: "#E5E7EB", strokeWidth: 1 }}
                />
                {/* Team series is always the per-athlete average so it shares a
                    scale with a selected athlete. Solid area on its own, dashed
                    reference line once an athlete is the primary series. */}
                {hasSelectedPlayer && !hidden.has("player") && (
                  <Area
                    type="monotone"
                    dataKey={isCumulative ? "playerCum" : "playerLoad"}
                    name={selectedPlayerName}
                    stroke={isCumulative ? PLAYER_COLOR : "url(#zoneStroke)"}
                    strokeWidth={2}
                    fill={
                      isCumulative ? "url(#playerLoadFill)" : "url(#zoneFill)"
                    }
                    isAnimationActive={false}
                    dot={false}
                    activeDot={{ r: 3, strokeWidth: 0 }}
                  />
                )}
                {!hidden.has("team") &&
                  (hasSelectedPlayer ? (
                    <Line
                      type="monotone"
                      dataKey={isCumulative ? "teamAvgCum" : "teamAvgLoad"}
                      name={t("session.load.legend.teamAvgPerAthlete")}
                      stroke={TEAM_COLOR}
                      strokeWidth={1.5}
                      strokeDasharray="5 4"
                      strokeOpacity={0.8}
                      isAnimationActive={false}
                      dot={false}
                      activeDot={{ r: 3, strokeWidth: 0 }}
                    />
                  ) : (
                    <Area
                      type="monotone"
                      dataKey={isCumulative ? "teamAvgCum" : "teamAvgLoad"}
                      name={t("session.load.legend.teamAvgPerAthlete")}
                      stroke={isCumulative ? TEAM_COLOR : "url(#zoneStroke)"}
                      strokeWidth={2}
                      fill={
                        isCumulative ? "url(#teamLoadFill)" : "url(#zoneFill)"
                      }
                      isAnimationActive={false}
                      dot={false}
                      activeDot={{ r: 3, strokeWidth: 0 }}
                    />
                  ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <FooterSlot>
          {isCumulative || !zoneShares ? (
            t(
              isCumulative
                ? "session.load.footer.summaryAccumulated"
                : "session.load.footer.summaryPerWindow",
              {
                threshold: binEdges[0] ?? 3,
                windows: data.length,
                count: nAthletes,
              }
            )
          ) : (
            <span className="flex items-center gap-3">
              <span>
                {hasSelectedPlayer
                  ? t("session.load.footer.playerIntensity", {
                      name: selectedPlayerName,
                    })
                  : t("session.load.footer.sessionIntensity")}
              </span>
              {/* Hover a zone to light up its band and its stretches of the
                  curve; everything else fades. */}
              {zoneShares.map((z) => {
                const active = hoverZone === z.key;
                const faded = hoverZone != null && !active;
                return (
                  <span
                    key={z.key}
                    onMouseEnter={() => setHoverZone(z.key)}
                    onMouseLeave={() => setHoverZone(null)}
                    className="flex cursor-default items-center gap-1 rounded-md px-1.5 py-0.5 transition duration-150"
                    style={{
                      backgroundColor: active ? `${z.color}14` : "transparent",
                      opacity: faded ? 0.4 : 1,
                    }}
                  >
                    <span
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: z.color }}
                    />
                    <span
                      style={{
                        color: active ? z.color : z.pct ? "#6B7280" : "#D1D5DB",
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      {t("session.load.footer.zoneShare", {
                        label: z.label,
                        pct: z.pct,
                      })}
                    </span>
                  </span>
                );
              })}
              <span className="text-[#D1D5DB]">·</span>
              <span>
                {t("session.load.footer.windowsAthletes", {
                  windows: data.length,
                  count: nAthletes,
                })}
              </span>
            </span>
          )}
        </FooterSlot>
      </div>
    </div>
  );
};

export default SessionLoadChart;
